import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  STORAGE_KEY,
  isLanguage,
  translate,
} from '../lib/i18n';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Everyone starts in English. There is deliberately no device-locale detection,
// so nobody is switched into another language unexpectedly.
const readStored = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLanguage(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStored);

  // Screen readers take pronunciation from this attribute.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((choice) => {
    if (!isLanguage(choice)) return;
    setLanguageState(choice);
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* private mode: the choice lasts for this session */
    }
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (key, vars) => translate(language, key, vars),
    }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
